<template>
  <div>

    <!-- Page header -->
    <div class="wz-page-header">
      <h1 class="wz-page-title">Calendario de vacaciones</h1>
    </div>

    <wz-card :body-padding="false">

      <!-- Toolbar: navegación de mes + filtros -->
      <div class="cal-toolbar">
        <!-- Navegación de mes -->
        <div class="cal-month-nav">
          <button type="button" class="cal-month-btn" @click="prevMonth">
            <i class="ri-arrow-left-s-line"></i>
          </button>
          <span class="cal-month-label">{{ monthLabel }}</span>
          <button type="button" class="cal-month-btn" @click="nextMonth">
            <i class="ri-arrow-right-s-line"></i>
          </button>
        </div>

        <div class="cal-toolbar-divider"></div>

        <!-- Filtros -->
        <div class="cal-filter-group">
          <wz-select
            v-model="filters.companyId"
            :items="companies"
            defaultOption="Todas las empresas"
            @change="onCompanySelected"
          />
        </div>
        <div class="cal-filter-group">
          <wz-select
            v-model="filters.areaId"
            :items="areas"
            defaultOption="Todas las áreas"
            @change="onAreaSelected"
          />
        </div>
        <wz-button variant="light" size="sm" @click="clearFilters">
          <i class="ri-refresh-line mr-1"></i>Limpiar
        </wz-button>
      </div>

      <!-- Empty state -->
      <div v-if="!loading && calendarRows.length === 0" class="cal-empty">
        <i class="ri-calendar-2-line cal-empty-icon"></i>
        <p class="cal-empty-title">No hay vacaciones programadas para este mes</p>
        <p class="cal-empty-sub">Intenta con otro mes o revisa los filtros aplicados.</p>
      </div>

      <!-- Grilla Gantt -->
      <div v-else-if="calendarRows.length > 0" class="gantt-outer">
        <div class="gantt-wrapper">

          <!-- Header: nombre + días -->
          <div class="gantt-row gantt-header" :style="gridStyle">
            <div class="gantt-col-name gantt-header-name">COLABORADOR</div>
            <div
              v-for="d in dayNumbers"
              :key="'h-' + d"
              class="gantt-header-day"
              :class="{ 'gantt-day--weekend': isWeekend(d), 'gantt-day--today': isToday(d) }"
            >{{ d }}</div>
          </div>

          <!-- Filas de empleados -->
          <div
            v-for="(row, rowIdx) in calendarRows"
            :key="rowIdx"
            class="gantt-row gantt-data-row"
            :style="gridStyle"
          >
            <div class="gantt-col-name" :title="row.employeeName">
              {{ row.employeeName }}
            </div>
            <div
              v-for="d in dayNumbers"
              :key="'d-' + d"
              class="gantt-day-cell"
              :class="{ 'gantt-day--weekend': isWeekend(d), 'gantt-day--today': isToday(d) }"
            ></div>
            <div
              v-for="(bar, barIdx) in row.bars"
              :key="'bar-' + barIdx"
              class="gantt-bar"
              :class="{
                'gantt-bar--approved': bar.status === 'APROBADA',
                'gantt-bar--pending':  bar.status === 'PENDIENTE',
                'gantt-bar--enjoyed':  bar.status === 'GOZADA',
              }"
              :style="barStyle(bar)"
              :title="bar.tooltip"
              @click="$router.push({ name: 'vacation-requests-detail', params: { id: bar.requestId } })"
            >
              <span v-if="bar.endDay - bar.startDay >= 2" class="gantt-bar-label">
                {{ bar.endDay - bar.startDay + 1 }}d
              </span>
            </div>
          </div>

        </div>

        <!-- Leyenda -->
        <div class="cal-legend">
          <div class="cal-legend-item">
            <span class="cal-legend-dot cal-legend-dot--approved"></span>
            <span>APROBADA</span>
          </div>
          <div class="cal-legend-item">
            <span class="cal-legend-dot cal-legend-dot--pending"></span>
            <span>PENDIENTE</span>
          </div>
          <div class="cal-legend-item">
            <span class="cal-legend-dot cal-legend-dot--enjoyed"></span>
            <span>GOZADA</span>
          </div>
        </div>
      </div>

    </wz-card>

  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import * as moment from 'moment'
import { VacationService, CompanyService, AreaService } from '@/core/services'

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
]

// Parseo local de fechas "YYYY-MM-DD". new Date(str) las interpreta como UTC medianoche,
// y en zonas UTC-negativas (Perú UTC-5) el día se corre al anterior (off-by-one en la barra:
// 01/07→30/07 se pintaba como 29 días). moment(str, 'YYYY-MM-DD') parsea en hora local.
function parseLocal(str) {
  return moment(str, 'YYYY-MM-DD').toDate()
}

export default {
  name: 'VacationCalendar',

  data() {
    const now = new Date()
    return {
      currentYear:  now.getFullYear(),
      currentMonth: now.getMonth(),
      requests:  [],
      companies: [],
      areas:     [],
      loading:   false,
      filters: { companyId: null, areaId: null },
    }
  },

  computed: {
    monthLabel() {
      return `${MONTH_NAMES[this.currentMonth]} ${this.currentYear}`
    },
    daysInMonth() {
      return new Date(this.currentYear, this.currentMonth + 1, 0).getDate()
    },
    dayNumbers() {
      return Array.from({ length: this.daysInMonth }, (_, i) => i + 1)
    },
    gridStyle() {
      return `grid-template-columns: 280px repeat(${this.daysInMonth}, minmax(30px, 1fr))`
    },
    calendarRows() {
      const year  = this.currentYear
      const month = this.currentMonth
      const firstDay = new Date(year, month, 1)
      const lastDay  = new Date(year, month + 1, 0)

      const relevant = this.requests.filter(r => {
        // Ventas de vacaciones: compensación económica sin descanso físico → no van en el calendario.
        if (r.requestType === 'VENTA') return false
        // GOZADA se incluye (contexto histórico) con estilo atenuado; se excluyen RECHAZADA/CANCELADA.
        if (r.status !== 'APROBADA' && r.status !== 'PENDIENTE' && r.status !== 'GOZADA') return false
        const start = parseLocal(r.startDate)
        const end   = parseLocal(r.endDate)
        return start <= lastDay && end >= firstDay
      })

      const map = {}
      relevant.forEach(r => {
        const key = r.employeeName || r.employeeId
        if (!map[key]) map[key] = { employeeName: r.employeeName, bars: [] }
        const start    = parseLocal(r.startDate)
        const end      = parseLocal(r.endDate)
        const startDay = start < firstDay ? 1 : start.getDate()
        const endDay   = end > lastDay ? lastDay.getDate() : end.getDate()
        map[key].bars.push({
          requestId: r.id,
          startDay, endDay,
          status:  r.status,
          tooltip: `${r.startDate}  →  ${r.endDate}  (${r.calendarDays} días)  ·  ${r.status}`,
        })
      })

      return Object.values(map).sort((a, b) =>
        (a.employeeName || '').localeCompare(b.employeeName || '')
      )
    },
  },

  methods: {
    async loadData() {
      try {
        this.loading = true
        this.$store.dispatch('app/loading', true)
        const params = { year: this.currentYear, month: this.currentMonth + 1 }
        if (this.filters.companyId) params.companyId = this.filters.companyId
        if (this.filters.areaId)    params.areaId    = this.filters.areaId
        const resp = await VacationService.getRequests(params)
        this.requests = resp.payload || []
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cargar el calendario')
      } finally {
        this.loading = false
        this.$store.dispatch('app/loading', false)
      }
    },
    prevMonth() {
      if (this.currentMonth === 0) { this.currentMonth = 11; this.currentYear-- }
      else this.currentMonth--
      this.loadData()
    },
    nextMonth() {
      if (this.currentMonth === 11) { this.currentMonth = 0; this.currentYear++ }
      else this.currentMonth++
      this.loadData()
    },
    isWeekend(day) {
      const dow = new Date(this.currentYear, this.currentMonth, day).getDay()
      return dow === 0 || dow === 6
    },
    isToday(day) {
      const now = new Date()
      return now.getFullYear() === this.currentYear && now.getMonth() === this.currentMonth && now.getDate() === day
    },
    barStyle(bar) {
      return `grid-column: ${bar.startDay + 1} / ${bar.endDay + 2}; grid-row: 1;`
    },
    onCompanySelected(id) { this.filters.companyId = id; this.loadData() },
    onAreaSelected(id)    { this.filters.areaId    = id; this.loadData() },
    clearFilters() {
      this.filters.companyId = null
      this.filters.areaId    = null
      this.loadData()
    },
  },

  async created() {
    try {
      const [companiesResp, areasResp] = await Promise.all([
        CompanyService.listAll(),
        AreaService.listAll(),
      ])
      this.companies = (companiesResp.payload || []).map(c => ({ key: c.id, label: c.businessName || c.name }))
      this.areas     = (areasResp.payload    || []).map(a => ({ key: a.id, label: a.description }))
    } catch (e) {
      console.error('Error al cargar catálogos del calendario', e)
    }
    this.loadData()
  },
}
</script>

<style scoped lang="stylus">

/* ── Navegación de mes ─────────────────────────── */
.cal-month-nav
  display flex
  align-items center
  gap 4px

.cal-month-btn
  width 32px
  height 32px
  border 1px solid var(--admin-border-color)
  border-radius var(--admin-border-radius, 6px)
  background var(--admin-bg-panel)
  color var(--admin-text-main)
  cursor pointer
  display flex
  align-items center
  justify-content center
  font-size 18px
  padding 0
  transition background 0.15s

  &:hover
    background var(--admin-bg-hover)

.cal-month-label
  font-size 15px
  font-weight 600
  min-width 150px
  text-align center
  color var(--admin-text-main)

/* ── Toolbar ────────────────────────────────────── */
.cal-toolbar
  display flex
  align-items center
  gap 12px
  flex-wrap wrap
  padding 12px 20px
  border-bottom 1px solid var(--admin-border-color)

.cal-toolbar-divider
  width 1px
  height 28px
  background var(--admin-border-color)
  flex-shrink 0

.cal-filter-group
  min-width 190px

  ::v-deep .wz-form-group
    margin-bottom 0

/* ── Empty state ────────────────────────────────── */
.cal-empty
  text-align center
  padding 60px 20px
  color var(--admin-text-muted)

.cal-empty-icon
  font-size 3rem
  opacity 0.35
  display block
  margin-bottom 12px

.cal-empty-title
  font-size 1.1rem
  font-weight 500
  margin-bottom 4px
  color var(--admin-text-main)

.cal-empty-sub
  font-size 0.875rem

/* ── Gantt contenedor ───────────────────────────── */
.gantt-outer
  width 100%

.gantt-wrapper
  overflow-x auto

/* ── Fila base ───────────────────────────────────── */
.gantt-row
  display grid
  position relative
  border-bottom 1px solid var(--admin-border-color)

  &:last-child
    border-bottom none

/* ── Columna nombre (sticky) ─────────────────────── */
.gantt-col-name
  position sticky
  left 0
  background var(--admin-bg-panel)
  z-index 3
  padding 0 14px
  display flex
  align-items center
  border-right 2px solid var(--admin-border-color)
  font-size 13px
  min-height 44px
  white-space nowrap
  overflow hidden
  text-overflow ellipsis
  max-width 280px
  color var(--admin-text-main)

/* ── Header del gantt ────────────────────────────── */
.gantt-header
  background var(--admin-bg-hover)
  border-bottom 2px solid var(--admin-border-color)

.gantt-header-name
  background var(--admin-bg-hover)
  font-weight 700
  font-size 11px
  letter-spacing 0.04em
  color var(--admin-text-muted)

.gantt-header-day
  display flex
  align-items center
  justify-content center
  font-size 11px
  font-weight 600
  color var(--admin-text-muted)
  border-right 1px solid var(--admin-border-color)
  min-height 36px
  background var(--admin-bg-hover)

  &.gantt-day--weekend
    background var(--admin-bg-base)
    color var(--admin-text-muted)
    opacity 0.7

  &.gantt-day--today
    background unquote("color-mix(in srgb, var(--admin-primary-color) 12%, transparent)")
    color var(--admin-primary-color)
    font-weight 700

/* ── Celda de día (filas de datos) ───────────────── */
.gantt-day-cell
  border-right 1px solid var(--admin-border-color)
  min-height 44px
  opacity 1

  &.gantt-day--weekend
    background var(--admin-bg-base)
    opacity 0.6

  &.gantt-day--today
    background unquote("color-mix(in srgb, var(--admin-primary-color) 6%, transparent)")

/* ── Barras de vacaciones ────────────────────────── */
.gantt-bar
  align-self center
  height 26px
  border-radius 5px
  cursor pointer
  z-index 4
  display flex
  align-items center
  overflow hidden
  transition opacity 0.15s, box-shadow 0.15s
  margin 0 2px

  &:hover
    opacity 0.85
    box-shadow 0 2px 8px rgba(0,0,0,0.18)

.gantt-bar--approved
  background #22c55e

/* GOZADA: mismo verde de APROBADA pero atenuado (contexto histórico). */
.gantt-bar--enjoyed
  background #22c55e
  opacity 0.4

  /* Hover intermedio: feedback visible sin igualar el 0.85 de APROBADA (mantiene contraste).
     Va después del &:hover base de .gantt-bar → gana por orden (misma especificidad). */
  &:hover
    opacity 0.6

.gantt-bar--pending
  background repeating-linear-gradient(
    45deg,
    #fbbf24 0px,
    #fbbf24 6px,
    #fef9c3 6px,
    #fef9c3 12px
  )
  border 1px solid #d97706

.gantt-bar-label
  font-size 10px
  font-weight 700
  color rgba(255,255,255,0.95)
  padding-left 6px
  white-space nowrap
  pointer-events none

.gantt-bar--pending .gantt-bar-label
  color #92400e

/* ── Leyenda ─────────────────────────────────────── */
.cal-legend
  display flex
  align-items center
  gap 20px
  padding 12px 20px
  border-top 1px solid var(--admin-border-color)
  font-size 12px
  font-weight 600
  letter-spacing 0.04em
  color var(--admin-text-muted)

.cal-legend-item
  display flex
  align-items center
  gap 7px

.cal-legend-dot
  width 16px
  height 16px
  border-radius 4px
  display inline-block
  flex-shrink 0

.cal-legend-dot--approved
  background #22c55e

.cal-legend-dot--enjoyed
  background #22c55e
  opacity 0.4

.cal-legend-dot--pending
  background repeating-linear-gradient(
    45deg,
    #fbbf24 0px,
    #fbbf24 6px,
    #fef9c3 6px,
    #fef9c3 12px
  )
  border 1px solid #d97706
</style>
